import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import Cards, { Focused } from 'react-credit-cards'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { CreditCardModel, KvkkDeclarationTemplate } from '../models/models'
import { GetMyCreditCardsApi } from '../services/getMyCreditCardsApi'
import IPButton from '../../../../components/IPButton'
import IPModal from '../../../../components/IPModal'
import IPInput from '../../../../components/IPInput'
import { useCustomReducer } from '../../../../base/customHooks'
import classes from '../../../../components/IPFile/IPFile.module.scss'
import { getUploadFileRequest } from '../../../../base/utils/dataHelper'
import { compactCardNumber, formatCardNumberGroups } from '../../../../base/utils/cardNumber'
import { AddCreditCardApi } from '../services/addCreditCardApi'
import { UpdateCreditCardApi } from '../services/updateCreditCardApi'
import { GetCreditCardDeclarationTemplateApi } from '../services/getCreditCardDeclarationTemplateApi'
import { GetCreditCardDeclarationApi } from '../services/getCreditCardDeclarationApi'
import IPCheckbox from '../../../../components/IPCheckBox'
import { DeleteCreditCardApi } from '../services/deleteCreditCardApi'
import { ChangeActivityCreditCardApi } from '../services/changeActivityCreditCardApi'
import IPImage from '../../../../components/IPImage'
import styles from './../../../MyCard/MyCard.module.scss'

// KVKK kart bildirim beyanının metni artık statik PDF/DOCX değil: backend'den blok blok
// geliyor ve bayi kart bilgilerini yazdıkça belge ekranda canlı doluyor. Kaydederken aynı
// metinden sunucu tarafında PDF üretilip saklanıyor — çıktı alıp imzalayıp tarama yok.

// Yüklenebilecek maksimum dosya boyutu (5 MB)
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024

interface ICardState {
  cardName?: string,
  cardNumber?: string,
  showModal: boolean
  kvkkApproved: boolean
  showKvkkModal: boolean
  // Düzenleme modu: yalnızca Beklemede (Status === 0) kartlar için açılır.
  showEditModal: boolean
  editCardId?: number
  // Kart üzerinde KVKK onayı zaten varsa geri alınamaz, checkbox kilitlenir.
  editKvkkAlreadyApproved: boolean
  editHasImage: boolean
  // Düzenlemede belge önizlemesi kartın kayıtlı bilgisini gösterir (numara maskeli gelir).
  editCardName?: string
  editCardNumber?: string
  File?: {
    Label: string
    Content: string
    Extension: string
    FileType: string
  }
}

function CreditCards(props: BaseComponentWithContextProps) {
  const getInitialState = (): ICardState => {
    return {
      showModal: false,
      kvkkApproved: false,
      showKvkkModal: false,
      showEditModal: false,
      editKvkkAlreadyApproved: false,
      editHasImage: false,
    }
  }

  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<ICardState>(intialState)

  const [myCards, setMyCards] = useState<CreditCardModel[]>([])
  // Belge metni backend'de tutuluyor; burada sadece render edilip kart alanları dolduruluyor.
  const [declarationTemplate, setDeclarationTemplate] = useState<KvkkDeclarationTemplate | null>(null)

  const getDeclarationTemplate = async () => {
    try {
      const templateApi: GetCreditCardDeclarationTemplateApi = new GetCreditCardDeclarationTemplateApi({})
      const result = await templateApi.getTemplate()
      if (result) setDeclarationTemplate(result)
    } catch {
      // Şablon gelmezse modal "yükleniyor" gösterir; kart ekleme akışını kırmasın.
    }
  }

  // Belge base64 geliyor (tam kart numarası içerdiği için statik URL yok); Blob'a çevirip açıyoruz.
  const openDeclaration = async (id: number) => {
    props.showLoading()
    try {
      const declarationApi: GetCreditCardDeclarationApi = new GetCreditCardDeclarationApi({})
      const result = await declarationApi.getDeclaration(id)

      if (result && result.ContentBase64) {
        const bytes = Uint8Array.from(atob(result.ContentBase64), c => c.charCodeAt(0))
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err.description,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
    props.hideLoading()
  }

  const documentCardName = (state.showEditModal ? state.editCardName : state.cardName) || '—'
  const documentCardNumber =
    formatCardNumberGroups(state.showEditModal ? state.editCardNumber : state.cardNumber) || '—'

  const getMyCards = async () => {
    props.showLoading()
    const getLicenseTracking: GetMyCreditCardsApi = new GetMyCreditCardsApi({})
    getLicenseTracking
      .getMyCreditCards()
      .then(result => {
        if (result) {
          setMyCards(result)
          props.hideLoading()
        }
      })
      .catch((err: any) => {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.hideLoading()
      })
    return
  }

  const saveCard = async () => {
    if (state.cardNumber === undefined || state.cardName === undefined || state.File === undefined) {
      Swal.fire({
        icon: 'error',
        title: 'Bütün alanlar zorunludur!',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const req = {
      CardNumber: state.cardNumber,
      CardName: state.cardName,
      Image: state.File,
      KvkkApproved: state.kvkkApproved
    }
    props.showLoading()
    const addCardApi: AddCreditCardApi = new AddCreditCardApi({})
    addCardApi
      .addCard(req)
      .then(result => {
        if (result) {
          setState({ showModal: false, cardName: undefined, cardNumber: undefined, File: undefined, kvkkApproved: false, showKvkkModal: false })
          Swal.fire({
            icon: 'success',
            title: 'Kartınız onaya gönderilmiştir.',
            showConfirmButton: true,
            allowOutsideClick: false,
          })
          props.hideLoading()
          getMyCards()
        }
      })
      .catch((err: any) => {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.hideLoading()
      })
    return
  }

  const closeEditModal = () => {
    setState({
      showEditModal: false,
      editCardId: undefined,
      editKvkkAlreadyApproved: false,
      editHasImage: false,
      File: undefined,
      kvkkApproved: false,
      showKvkkModal: false,
    })
  }

  const openEditModal = (item: CreditCardModel) => {
    setState({
      showEditModal: true,
      editCardId: item.Id,
      editKvkkAlreadyApproved: item.KvkkApproved,
      editHasImage: !!item.Image,
      editCardName: item.CardName,
      editCardNumber: item.CardNumber,
      // Dosya alanları boş başlar: gönderilmezse backend mevcut belgeyi korur.
      File: undefined,
      kvkkApproved: item.KvkkApproved,
      showKvkkModal: false,
    })
  }

  const updateCard = async () => {
    if (!state.editCardId) return

    const kvkkNewlyApproved = !state.editKvkkAlreadyApproved && state.kvkkApproved
    if (state.File === undefined && !kvkkNewlyApproved) {
      Swal.fire({
        icon: 'info',
        title: 'Bir değişiklik yapmadınız.',
        text: 'Kart görselini yükleyin veya KVKK onayını verin.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const req = {
      Id: state.editCardId,
      Image: state.File,
      KvkkApproved: state.kvkkApproved,
    }

    props.showLoading()
    const updateApi: UpdateCreditCardApi = new UpdateCreditCardApi({})
    updateApi
      .updateCard(req)
      .then(result => {
        if (result) {
          closeEditModal()
          Swal.fire({
            icon: 'success',
            title: 'Kartınız güncellenmiştir.',
            showConfirmButton: true,
            allowOutsideClick: false,
          })
          props.hideLoading()
          getMyCards()
        }
      })
      .catch((err: any) => {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.hideLoading()
      })
    return
  }

  useEffect(() => {
    getMyCards()
    getDeclarationTemplate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const handleFiles = async (e: any) => {
    const selected = e?.target?.files?.[0]
    if (selected) {
      const name = selected.name.toLowerCase()
      const isImage =
        selected.type === 'image/jpeg' ||
        selected.type === 'image/png' ||
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        name.endsWith('.png')
      if (!isImage) {
        Swal.fire({
          icon: 'error',
          title: 'Kart resmi yalnızca JPG, JPEG veya PNG formatında yüklenebilir.',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        e.target.value = ''
        return
      }
      if (selected.size > MAX_FILE_SIZE) {
        Swal.fire({
          icon: 'error',
          title: `Kart resmi en fazla ${MAX_FILE_SIZE_MB} MB olabilir.`,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        e.target.value = ''
        return
      }
    }
    const fileList: any[] = await getUploadFileRequest(e)
    if (fileList && fileList.length > 0) {
      setState({
        File: fileList[0]
      })
    }
  }

  const deleteCard = async (id: number) => {
    Swal.fire({
      icon: 'question',
      title: 'Kartınız silmek istediğinizden emin misiniz?.',
      showConfirmButton: true,
      showDenyButton: true,
      allowOutsideClick: false,
      denyButtonText: 'Hayır',
      confirmButtonText: 'Evet'
    }).then(async result => {
      if (result.isConfirmed) {
        props.showLoading()
        const deleteApi: DeleteCreditCardApi = new DeleteCreditCardApi({})
        deleteApi
          .deleteCard(id)
          .then(result => {
            if (result) {
              Swal.fire({
                icon: 'success',
                title: 'Kartınız silinmiştir.',
                showConfirmButton: true,
                allowOutsideClick: false,
              })
              props.hideLoading()
              getMyCards()
            }
          })
          .catch((err: any) => {
            Swal.fire({
              icon: 'error',
              title: err.description,
              showConfirmButton: true,
              allowOutsideClick: false,
            })
            props.hideLoading()
          })
        return
      }
    })
  }


  const changeCard = async (id: number) => {
    props.showLoading()
    const changeApi: ChangeActivityCreditCardApi = new ChangeActivityCreditCardApi({})
    changeApi
      .changeActivityCard(id)
      .then(result => {
        if (result) {
          Swal.fire({
            icon: 'success',
            title: 'Kartınızın aktifliği değiştirilmiştir.',
            showConfirmButton: true,
            allowOutsideClick: false,
          })
          props.hideLoading()
          getMyCards()
        }
      })
      .catch((err: any) => {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.hideLoading()
      })
    return
  }


  enum bankNames {
    akbank = '46',
    halkbank = '12',
    denizbank = '134',
    Akbank = '46',
    AlbarakaTurk = '203',
    AlternatifBank = '124',
    Anadolubank = '135',
    Denizbank = '134',
    Fibabanka = '103',
    QNBFinansbank = '111',
    GarantiBBVA = '62',
    Halkbank = '12',
    HSBC = '123',
    INGBank = '99',
    IsBankasi = '64',
    KuveytTurk = '205',
    Odeabank = '146',
    TurkEkonomiBankasi = '32',
    TurkiyeFinans = '206',
    Vakifbank = '15',
    YapiKrediBankasi = '67',
    Sekerbank = '59',
    ZiraatBankasi = '10',
    AktifYatirimBankasi = '143',
  }

  const bankImages: { [key: string]: string } = {
    [bankNames.akbank]: '/images/akbank.png',
    [bankNames.halkbank]: '/images/halkbank.png',
    [bankNames.denizbank]: '/images/denizBank.png',
    [bankNames.AlbarakaTurk]: '/images/product-detail/alBaraka.svg',
    [bankNames.AlternatifBank]: '/images/product-detail/alternatifBank.svg',
    [bankNames.Anadolubank]: '/images/product-detail/anadoluBank.svg',
    [bankNames.Fibabanka]: '/images/product-detail/fibabanka.svg',
    [bankNames.QNBFinansbank]: '/images/finansbank.png',
    [bankNames.GarantiBBVA]: '/images/garantiBankasi.png',
    [bankNames.HSBC]: '/images/product-detail/hsbcBank.svg',
    [bankNames.INGBank]: '/images/product-detail/ingBank.svg',
    [bankNames.IsBankasi]: '/images/isBank.png',
    [bankNames.KuveytTurk]: '/images/product-detail/kuveytTurk.svg',
    [bankNames.Odeabank]: '/images/product-detail/odeabank.svg',
    [bankNames.TurkEkonomiBankasi]: '/images/product-detail/teb.svg',
    [bankNames.TurkiyeFinans]: '/images/product-detail/turkiyeFinansBank.svg',
    [bankNames.Vakifbank]: '/images/product-detail/vakifBank.svg',
    [bankNames.YapiKrediBankasi]: '/images/yapiKredi.png',
    [bankNames.Sekerbank]: '/images/product-detail/sekerBank.svg',
    [bankNames.ZiraatBankasi]: '/images/ZiraatBankasi.png',
    [bankNames.AktifYatirimBankasi]: '/images/product-detail/aktifBank.svg',
  };

  const getBankImageUrl = (bankCode: string): string => {
    return bankImages[bankCode] || '';
  }

  return (
    <div>
      <div className='p-4 d-flex justify-content-between align-items-center'>
        <span className='h4 mb-0'>Kredi Kartlarım</span>
        <IPButton
          className='btn btn border-0'
          style={{
            backgroundColor: 'rgb(238, 51, 62)',
            width: '150px',
          }}
          text={'Kart Ekle'}
          onClick={() => {
            setState({ showModal: true })
          }}
        />
      </div>
      <div className='row'>
        {myCards &&
          myCards.map((item) => (
            <div className='col-sm-6 col-lg-6 mb-4' key={item.Id}>
              <div className='card p-5'>
                {item.bankLogo != "0" &&
                  <IPImage
                    alt=''
                    src={getBankImageUrl(item.bankLogo)}
                    className={styles.banksImage}
                    style={{
                      position: 'absolute',
                      top: '25%',
                      left: '45%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: '999',
                      maxWidth: '90px',
                    }}
                  />
                }
                <b style={{ textAlign: 'center' }}>{item.bankName}</b>
                <br></br>
                <Cards
                  cvc={''}
                  expiry={''}
                  focused={'number'}
                  name={item.CardName}
                  placeholders={{ name: 'AD SOYAD' }}
                  number={item.CardNumber}
                />
                <div className='row m-t-10 m-l-10'>
                  {item.KvkkApproved ? (
                    <span style={{ color: 'green', fontWeight: 600 }}>
                      KVKK Onaylandı
                      {item.KvkkApprovalDate ? ` (${moment(item.KvkkApprovalDate).format('DD.MM.YYYY')})` : ''}
                    </span>
                  ) : (
                    <span style={{ color: 'gray', fontWeight: 600 }}>KVKK onayı verilmedi</span>
                  )}
                </div>
                {item.Active ? (
                  <div className='row m-t-10 m-l-10 p-10'>
                    <span className='h5'>
                      <h5 style={{ color: item.Status === 0 ? "orange" : item.Status === 1 ? "red" : item.Status === 2 ? "green" : "" }}>
                        {item.Status === 0 ? "Beklemede " : item.Status === 1 ? "Reddedildi " : item.Status === 2 ? "Onaylandı " : " "}
                        - (Ekleyen: {item.UserName})
                      </h5>
                    </span>
                    {item.Status === 1 && item.RejectReason && (
                      <div className='row m-l-10' style={{ width: '100%' }}>
                        <small style={{ color: 'rgb(238, 51, 62)' }}>
                          <b>Red nedeni:</b> {item.RejectReason}
                        </small>
                      </div>
                    )}
                    {/* Onay belgesi artık bayiden istenmiyor: KVKK onayı verildiğinde sunucu üretiyor. */}
                    {item.Status === 0 && (!item.Image || !item.KvkkApproved) && (
                      <div className='row m-l-10' style={{ width: '100%' }}>
                        <small style={{ color: 'rgb(238, 51, 62)' }}>
                          Eksik:{' '}
                          {[!item.Image ? 'kart görseli' : null, !item.KvkkApproved ? 'KVKK onayı' : null]
                            .filter(Boolean)
                            .join(', ')}
                          . Onaylanabilmesi için tamamlamanız gerekiyor.
                        </small>
                      </div>
                    )}
                    {item.HasDeclaration && (
                      <div className='row m-l-10' style={{ width: '100%' }}>
                        <small
                          style={{ color: 'rgb(238, 51, 62)', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => openDeclaration(item.Id)}
                        >
                          Onayladığınız kart bildirim beyanını görüntüle
                        </small>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        gap: '12px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span className='h5 mb-0'>
                        <IPCheckbox
                          label='Aktif'
                          onClick={() => changeCard(item.Id)}
                          checked={item.Enabled}
                        />
                      </span>
                      {/* Butonlar yan yana ve nefes alacak aralıkta dursun. */}
                      <div className='d-flex align-items-center' style={{ gap: '12px' }}>
                        {item.Status === 0 && (
                          <IPButton
                            className='btn btn border-0'
                            style={{ backgroundColor: '#6c757d', minWidth: '110px' }}
                            text={"Düzenle"}
                            onClick={() => openEditModal(item)}
                          />
                        )}
                        <IPButton
                          className='btn btn border-0'
                          style={{ backgroundColor: 'rgb(238, 51, 62)', minWidth: '110px' }}
                          text={"Sil"}
                          onClick={() => deleteCard(item.Id)}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='row m-t-10 m-l-10 p-10'>
                    <span className='h5'>
                      <h5 style={{ color: 'red' }}>Kartınız yönetici tarafından kullanıma kapatılmıştır.</h5>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
      <IPModal
        title={'Kart Ekle'}
        show={state.showModal}
        onClose={() =>
          setState({ showModal: false, cardName: undefined, cardNumber: undefined, File: undefined, kvkkApproved: false, showKvkkModal: false })
        }
        onConfirm={saveCard}
        confirmText={'Kaydet'}
        width={700}
      >
        <form className='p-3'>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label>Kart Sahibi</label>
              <IPInput
                type='text'
                className='form-control'
                name='NameAndSurname'
                required
                value={state.cardName}
                onChange={(e) => {
                  setState({ cardName: e.target.value })
                }}
              />
            </div>
            <div className='form-group col-md-6'>
              <label>Kart Numarası</label>
              <IPInput
                type='text'
                className='form-control'
                name='cardNumber'
                required
                inputMode='numeric'
                autoComplete='off'
                maxLength={23}
                controlledField
                placeholder='0000 0000 0000 0000'
                value={formatCardNumberGroups(state.cardNumber)}
                onChange={(e) => {
                  // Gösterim 4'erli gruplu, state ve servise giden değer birleşik.
                  setState({ cardNumber: compactCardNumber(e.target.value) })
                }}
              />
            </div>
            <div className='col-md-6 mb-2'>
              <div className={classes.file}>
                <input
                  type='file'
                  accept='image/jpeg,image/png,.jpg,.jpeg,.png'
                  onInput={(e) => handleFiles(e)}
                  multiple={false}
                  id={`DocumentFile`}
                />
                <label htmlFor={`DocumentFile`}>Resim Yükle (JPG, PNG)</label>
              </div>
              <small style={{ color: '#6c757d' }}>Maksimum dosya boyutu {MAX_FILE_SIZE_MB} MB'dır.</small>
              <p>
                {state.File && state.File.Label.length > 0 && <p>{state.File.Label}</p>}
              </p>
            </div>
            <div className='col-lg-12'>
              <label style={{ color: 'red' }}>Lütfen kart numarası ve kart ismi görünecek şekilde kartınızın resmini yükleyiniz.</label>
            </div>
            <div className='col-lg-12 mt-2'>
              <div className='d-flex align-items-center'>
                <IPCheckbox
                  name='kvkkApproved'
                  checked={state.kvkkApproved}
                  onCheckedChange={(checked: boolean) => setState({ kvkkApproved: checked })}
                />
                <div className='ml-2'>
                  <span
                    style={{ color: 'rgb(238, 51, 62)', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setState({ showKvkkModal: true })}
                  >
                    KVKK Aydınlatma Metni
                  </span>
                  'ni okudum ve onaylıyorum.
                </div>
                {state.kvkkApproved ? <span className='ml-2'>✅</span> : ''}
              </div>
            </div>
          </div>
        </form>
      </IPModal>
      <IPModal
        title={'Kartı Düzenle'}
        show={state.showEditModal}
        onClose={closeEditModal}
        onConfirm={updateCard}
        confirmText={'Güncelle'}
        width={700}
      >
        <form className='p-3'>
          <div className='form-row'>
            <div className='col-lg-12 mb-3'>
              <label>
                Kart numarası ve kart ismi değiştirilemez. Yalnızca eksik belgelerinizi yükleyebilir ve
                KVKK onayı verebilirsiniz.
              </label>
            </div>
            <div className='col-md-6 mb-2'>
              <div className={classes.file}>
                <input
                  type='file'
                  accept='image/jpeg,image/png,.jpg,.jpeg,.png'
                  onInput={(e) => handleFiles(e)}
                  multiple={false}
                  id={`EditDocumentFile`}
                />
                <label htmlFor={`EditDocumentFile`}>Resim Yükle (JPG, PNG)</label>
              </div>
              <small style={{ color: '#6c757d' }}>Maksimum dosya boyutu {MAX_FILE_SIZE_MB} MB'dır.</small>
              <p>
                {state.File && state.File.Label.length > 0 ? (
                  <p>{state.File.Label}</p>
                ) : (
                  <small style={{ color: state.editHasImage ? 'green' : 'rgb(238, 51, 62)' }}>
                    {state.editHasImage ? 'Yüklü — değiştirmek istemiyorsanız boş bırakın.' : 'Yüklenmemiş.'}
                  </small>
                )}
              </p>
            </div>
            <div className='col-lg-12'>
              <label style={{ color: 'red' }}>Lütfen kart numarası ve kart ismi görünecek şekilde kartınızın resmini yükleyiniz.</label>
            </div>
            <div className='col-lg-12 mt-2'>
              <div className='d-flex align-items-center'>
                <IPCheckbox
                  name='editKvkkApproved'
                  checked={state.kvkkApproved}
                  disabled={state.editKvkkAlreadyApproved}
                  onCheckedChange={(checked: boolean) => {
                    // Verilmiş onay geri alınamaz; backend de bunu reddediyor.
                    if (state.editKvkkAlreadyApproved) return
                    setState({ kvkkApproved: checked })
                  }}
                />
                <div className='ml-2'>
                  <span
                    style={{ color: 'rgb(238, 51, 62)', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setState({ showKvkkModal: true })}
                  >
                    KVKK Aydınlatma Metni
                  </span>
                  'ni okudum ve onaylıyorum.
                </div>
                {state.kvkkApproved ? <span className='ml-2'>✅</span> : ''}
              </div>
              {state.editKvkkAlreadyApproved && (
                <small style={{ color: '#6c757d' }}>
                  KVKK onayı verilmiş ve geri alınamaz. Onayı kaldırmak için kartı silmeniz gerekir.
                </small>
              )}
            </div>
          </div>
        </form>
      </IPModal>
      <IPModal
        title={'KVKK Aydınlatma Metni'}
        width={1200}
        show={state.showKvkkModal}
        confirmText={'Onayla'}
        onConfirm={() => setState({ kvkkApproved: true, showKvkkModal: false })}
        onClose={() => setState({ showKvkkModal: false })}
        closeOnEscapeKey={true}
        checkbox={
          <div className='d-flex align-items-center'>
            <IPCheckbox
              name='kvkkApprovedModal'
              checked={state.kvkkApproved}
              onCheckedChange={(checked: boolean) => setState({ kvkkApproved: checked })}
            />
            <div
              onClick={() => setState({ kvkkApproved: true })}
              className='ml-3 mb-2'
              style={{ cursor: 'pointer' }}
            >
              KVKK metnini okudum ve onaylıyorum.
            </div>
          </div>
        }
      >
        <div style={{ height: '80vh', overflowY: 'auto', padding: '0 8px' }}>
          {!declarationTemplate ? (
            <p>Belge yükleniyor…</p>
          ) : (
            <div style={{ fontSize: '13px', lineHeight: 1.6, textAlign: 'justify' }}>
              {declarationTemplate.Blocks.map((block, i) => {
                if (block.Type === 'heading')
                  return (
                    <h5 key={i} style={{ marginTop: '18px', marginBottom: '8px', fontWeight: 700 }}>
                      {block.Text}
                    </h5>
                  )
                if (block.Type === 'subheading')
                  return (
                    <h6 key={i} style={{ marginTop: '12px', marginBottom: '4px', fontWeight: 700 }}>
                      {block.Text}
                    </h6>
                  )
                if (block.Type === 'bullet')
                  return (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                      <span>•</span>
                      <span>{block.Text}</span>
                    </div>
                  )
                return (
                  <p key={i} style={{ marginBottom: '6px' }}>
                    {block.Text}
                  </p>
                )
              })}

              {/* Bayi yazdıkça dolan bölüm. Kaydederken sunucu aynı metinden PDF üretip saklıyor. */}
              <h5 style={{ marginTop: '22px', marginBottom: '8px', fontWeight: 700 }}>
                KREDİ KARTI BİLGİ FORMU
              </h5>
              <table className='table table-sm table-bordered' style={{ maxWidth: '640px' }}>
                <tbody>
                  <tr>
                    <th style={{ width: '220px' }}>Bayi Ünvanı</th>
                    <td>{declarationTemplate.DealerTitle || '-'}</td>
                  </tr>
                  <tr>
                    <th>Bayi Kodu</th>
                    <td>{declarationTemplate.DealerCode || '-'}</td>
                  </tr>
                  <tr>
                    <th>Kart Hamili Adı Soyadı</th>
                    <td>{documentCardName}</td>
                  </tr>
                  <tr>
                    <th>Kart Numarası</th>
                    <td>{documentCardNumber}</td>
                  </tr>
                </tbody>
              </table>

              <small style={{ color: '#6c757d' }}>
                Bu belge onayınızla birlikte elektronik ortamda oluşturulur; onaylayan kullanıcı,
                onay tarihi ve IP adresi belgenin altına kaydedilir. Çıktı alıp imzalamanıza gerek
                yoktur.
              </small>
            </div>
          )}
        </div>
      </IPModal>
    </div>
  )

}
export default componentWithContext(CreditCards)
